import React, { useEffect, useState } from 'react'
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { CCol, CForm, CFormInput, CFormLabel, CRow } from '@coreui/react'
import { Dialog } from 'primereact/dialog';
import { Button } from '@mui/material';
import { useModifier } from '../contexts/ModifiersContext'
import { useAuth } from '../contexts/AuthContext';
import CInput from './CInput';
import ModifierSelectionForModifierGroup from './ModifierSelectionForModifierGroup';
import { Checkbox } from 'primereact/checkbox';

function AddModifierGroup({ id, modifierDetail, editModifierId, visible, setVisible, setEditModifierId, editMode, setEditMode, setModifierDetail, fetchModifiers, canCreate, canUpdate, canDelete }) {
    const { getSingleModifier, deleteModifier, createModifierGroup, updateModifierGroup } = useModifier()
    const { toast, restaurantId } = useAuth()

    const [modifierValidated, setModifierValidated] = useState(false)
    const [validated, setValidated] = useState(false);

    const handleClose = () => {
        setVisible(false);
        setEditMode(false);
        setEditModifierId(null);
        setModifierDetail({
            name: "",
            display_name: "",
            price: 0,
            multi_select: false,
            min: null,
            max: null,
            no_max: false,
            modifiers: [],
            restaurant_id: restaurantId,
            auto_popup: false,
        })
    };

    const fetchSingleModifier = async () => {
        try {
            const modifier = await getSingleModifier(id);
            setModifierDetail(modifier);
        } catch (error) {
            console.error('Error fetching single modifier group', error);
        }
    };

    useEffect(() => {
        if (editMode && editModifierId) {
            fetchSingleModifier();
        }
    }, [editMode, editModifierId]);

    const handleDelete = async () => {
        try {
            const confirmed = await confirmDelete();

            if (confirmed) {
                const data = await deleteModifier(id);
                if (!data.error) {
                    setTimeout(function () {
                        toast.current?.show({ severity: 'success', summary: 'Modifier Group', detail: 'Modifier group delete successfully.', life: 3000 })
                    }, 500);
                    handleClose();
                    fetchModifiers()
                } else {
                    toast.current?.show({ severity: 'error', summary: 'Modifier Group', detail: data.message, life: 3000 })
                }
            }
        } catch (error) {
            console.error('Error during delete operation', error);
        }
    };

    const confirmDelete = async () => {
        return new Promise((resolve) => {
            confirmDialog({
                message: 'Are you sure you want to delete this modifier group?',
                header: 'Delete Confirmation',
                icon: 'pi pi-info-circle',
                position: 'top',
                accept: () => resolve(true),
                reject: () => resolve(false),
            });
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if ((editMode && !canUpdate) || (!editMode && !canCreate)) return;

        const form = e.currentTarget;
        setValidated(true);
        modifierDetail.modifiers.length > 0 ? setModifierValidated(false) : setModifierValidated(true)

        if (form.checkValidity() === false) {
            e.stopPropagation();
            const firstInvalidInput = form.querySelector(':invalid');
            if (firstInvalidInput) {
                firstInvalidInput.focus();
                firstInvalidInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        } else {
            try {
                if (visible && restaurantId && modifierDetail.modifiers.length > 0) {

                    const selectedModifiers = modifierDetail.modifiers.map(modifier => modifier.value)
                    const data = await createModifierGroup({ ...modifierDetail, restaurant_id: restaurantId, modifiers: selectedModifiers })
                    if (!data.error) {
                        fetchModifiers(data.modifier)
                        handleClose()
                    }
                } else if (editMode && editModifierId && restaurantId && modifierDetail.modifiers.length > 0) {

                    const selectedModifiers = modifierDetail.modifiers.map(modifier => modifier.value)

                    const data = await updateModifierGroup(id, { ...modifierDetail, restaurant_id: restaurantId, modifiers: selectedModifiers })
                    if (!data.error) {
                        handleClose()
                        fetchModifiers()
                    }
                }
            } catch (error) {
                console.log(error)
            }
        }
    }

    return (
        <div>
            <ConfirmDialog />
            <Dialog draggable={false} header={`${visible ? 'Create Modifier Group' : 'Edit Modifier Group'}`} visible={visible || (editMode && editModifierId !== null)} style={{ width: '35vw' }} onHide={handleClose}>
                <CForm onSubmit={handleSubmit} noValidate validated={validated}>
                    <div className='fields-container'>
                        <CRow className='mb-4 mt-4'>
                            <CInput
                                label={'Name'}
                                type={'text'}
                                className={'d-flex'}
                                labelClassName={'w-50'}
                                value={modifierDetail.name}
                                onChange={(value) => setModifierDetail({ ...modifierDetail, name: value })}
                                required={true}
                                errorMessage='Name is required.'
                            />
                        </CRow>
                        <CRow className='mb-4'>
                            <CInput
                                label={'Display Name'}
                                type={'text'}
                                className={'d-flex'}
                                labelClassName={'w-50'}
                                value={modifierDetail.display_name}
                                onChange={(value) => setModifierDetail({ ...modifierDetail, display_name: value })}
                            />
                        </CRow>
                        <CRow className='mb-4'>
                            <ModifierSelectionForModifierGroup
                                className='d-flex'
                                labelClassName='w-50'
                                value={modifierDetail.modifiers}
                                onChange={(value) => setModifierDetail({ ...modifierDetail, modifiers: value })}
                                required={true}
                                error={modifierValidated}
                            />
                        </CRow>
                        <CRow className='mb-4'>
                            <CCol lg={4}>
                                <CFormLabel className="form-label">Multi Select</CFormLabel>
                            </CCol>
                            <CCol lg={8} className='custom-checkbox px-1'>
                                <Checkbox
                                    checked={modifierDetail.multi_select === 1}
                                    onChange={(event) => {
                                        if (!event.target.checked) {
                                            setModifierDetail({ ...modifierDetail, multi_select: event.target.checked ? 1 : 0, min: null, max: null })
                                        } else {

                                            setModifierDetail({ ...modifierDetail, multi_select: event.target.checked ? 1 : 0 })
                                        }
                                    }}
                                />
                            </CCol>
                        </CRow>
                        {
                            modifierDetail.multi_select === 1 && (
                                <CRow className='mb-4'>
                                    <CCol lg={4}>
                                        <CFormLabel>Min / Max</CFormLabel>
                                    </CCol>
                                    <CCol lg={2} className='min px-1'>
                                        <CFormInput
                                            type={'text'}
                                            value={modifierDetail.min}
                                            // min={0}
                                            onChange={(e) => {
                                                const newValue = e.target.value;
                                                const sanitizedValue = newValue.replace(/[^0-9.]/g, '');
                                                if (sanitizedValue !== '') {
                                                    const numericValue = parseFloat(sanitizedValue);
                                                    setModifierDetail({ ...modifierDetail, min: numericValue });
                                                } else {
                                                    setModifierDetail({ ...modifierDetail, min: '' });
                                                }
                                            }}
                                            feedbackInvalid='Please enter a valid number.'
                                        />

                                    </CCol>
                                    <CCol lg={2}>
                                        <CFormInput
                                            type={'text'}
                                            value={modifierDetail.max}
                                            disabled={modifierDetail.no_max === 1}
                                            // min={0}
                                            onChange={(e) => {
                                                const newValue = e.target.value;
                                                const sanitizedValue = newValue.replace(/[^0-9.]/g, '');
                                                if (sanitizedValue !== '') {
                                                    const numericValue = parseFloat(sanitizedValue);
                                                    setModifierDetail({ ...modifierDetail, max: numericValue });
                                                } else {
                                                    setModifierDetail({ ...modifierDetail, max: '' });
                                                }
                                            }}
                                            feedbackInvalid='Please enter valid number.'
                                        />
                                    </CCol>
                                    <CCol lg={4} className='d-flex'>
                                        <Checkbox
                                            checked={modifierDetail.no_max === 1}
                                            onChange={(event) => {
                                                if (event.target.checked === true) {
                                                    setModifierDetail({ ...modifierDetail, max: "", no_max: event.target.checked ? 1 : 0 })
                                                } else {
                                                    setModifierDetail({ ...modifierDetail, no_max: event.target.checked ? 1 : 0 })
                                                }
                                            }
                                            }
                                        />
                                        <CFormLabel className="form-label ms-2">No max value</CFormLabel>
                                    </CCol>
                                </CRow>
                            )
                        }
                        <CRow className='mb-4'>
                            <CCol lg={4}>
                                <CFormLabel className="form-label">Auto Popup</CFormLabel>
                            </CCol>
                            <CCol lg={8} className='custom-checkbox px-1'>
                                <Checkbox
                                    checked={modifierDetail.auto_popup === 1}
                                    onChange={(event) => setModifierDetail({ ...modifierDetail, auto_popup: event.target.checked ? 1 : 0 })}
                                />
                            </CCol>
                        </CRow>
                    </div>
                    <div className={`d-flex mt-4 button-container ${(editMode && editModifierId) ? 'justify-content-between' : 'justify-content-end'}`}>
                        {
                            (editMode && editModifierId) && (
                                <div>
                                    {
                                        canDelete &&
                                        <Button color='error' variant="contained" onClick={handleDelete} className='me-2'>
                                            Delete
                                        </Button>
                                    }
                                    {/* <Button color='info' variant="contained" className='me-2'>
                                        Copy
                                    </Button> */}
                                </div>
                            )
                        }
                        <div className='d-flex'>
                            <Button onClick={handleClose} fullWidth variant="contained" color="inherit" >
                                Cancel
                            </Button>
                            {((!editMode && canCreate) || (editMode && canUpdate)) && (
                                <Button fullWidth variant="contained" color="primary" type="submit" className="ms-3">
                                    Submit
                                </Button>
                            )}
                        </div>
                    </div>
                </CForm>
            </Dialog>
        </div>
    )
}

export default AddModifierGroup